import React from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  DollarSign,
  MessageCircle,
  Unlock,
  ArrowUpCircle,
  AlertTriangle,
  TrendingUp,
  Clock,
  CheckCircle,
  CreditCard,
  Receipt,
  Play,
  RotateCcw,
  Award,
  ChevronRight,
} from "lucide-react";

const ICONS = {
  "dollar-sign": DollarSign,
  "message-circle": MessageCircle,
  "unlock": Unlock,
  "arrow-up-circle": ArrowUpCircle,
  "alert-triangle": AlertTriangle,
  "trending-up": TrendingUp,
  "clock": Clock,
  "check-circle": CheckCircle,
  "credit-card": CreditCard,
  "receipt": Receipt,
  "play": Play,
  "rotate-ccw": RotateCcw,
  "activity": Activity,
  "award": Award,
};

// Deterministic color per event category so the timeline reads at a glance
// without breaking the current dark orange palette.
const TONE = {
  sale: { ring: "bg-emerald-500/20", text: "text-emerald-400" },
  lead: { ring: "bg-sky-500/20", text: "text-sky-400" },
  stage_unlock: { ring: "bg-[#FF6B00]/20", text: "text-[#FF6B00]" },
  budget_increase: { ring: "bg-emerald-500/20", text: "text-emerald-400" },
  risk_assumed: { ring: "bg-red-500/20", text: "text-red-400" },
  achievement: { ring: "bg-yellow-500/20", text: "text-yellow-400" },
  diagnostic: { ring: "bg-yellow-500/20", text: "text-yellow-400" },
  warning: { ring: "bg-yellow-500/20", text: "text-yellow-400" },
  subscription_activated: { ring: "bg-emerald-500/20", text: "text-emerald-400" },
  subscription_active: { ring: "bg-emerald-500/20", text: "text-emerald-400" },
  subscription_trialing: { ring: "bg-sky-500/20", text: "text-sky-400" },
  subscription_past_due: { ring: "bg-yellow-500/20", text: "text-yellow-400" },
  subscription_unpaid: { ring: "bg-red-500/20", text: "text-red-400" },
  subscription_canceled: { ring: "bg-red-500/20", text: "text-red-400" },
  subscription_incomplete: { ring: "bg-yellow-500/20", text: "text-yellow-400" },
  subscription_cancel_scheduled: { ring: "bg-yellow-500/20", text: "text-yellow-400" },
  subscription_cancel_reverted: { ring: "bg-emerald-500/20", text: "text-emerald-400" },
  invoice_paid: { ring: "bg-emerald-500/20", text: "text-emerald-400" },
  invoice_payment_failed: { ring: "bg-red-500/20", text: "text-red-400" },
};

const DEFAULT_TONE = { ring: "bg-gray-500/20", text: "text-gray-300" };

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "Agora";
  if (mins < 60) return `há ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `há ${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days === 1) return "Ontem";
  if (days < 7) return `há ${days} dias`;
  return then.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export default function TimelineCard({ events = [] }) {
  if (!events || events.length === 0) {
    return (
      <div data-testid="timeline-card" className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-extrabold uppercase tracking-wider text-white flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-[#FF6B00]" />
            <span>Linha do Tempo da Operação</span>
          </span>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed">
          Sua operação ainda não registrou eventos. Cadastre um lead ou venda para começar a preencher sua linha do tempo.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="timeline-card" className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-extrabold uppercase tracking-wider text-white flex items-center gap-1.5">
          <Activity className="w-4 h-4 text-[#FF6B00]" />
          <span>Linha do Tempo da Operação</span>
        </span>
        <Link
          to="/operacao"
          data-testid="timeline-view-all-btn"
          className="text-[11px] font-bold text-[#FF6B00] hover:text-[#FF8533] flex items-center gap-0.5"
        >
          <span>Ver tudo</span>
          <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      <ul className="relative space-y-3 border-l border-[#282D3D] pl-4">
        {events.slice(0, 8).map((ev, idx) => {
          const Icon = ICONS[ev.icon] || Activity;
          const tone = TONE[ev.event_type] || DEFAULT_TONE;
          return (
            <li
              key={ev.id || ev._id || idx}
              data-testid={`timeline-event-${ev.event_type}`}
              className="relative"
            >
              <span
                className={`absolute -left-[27px] top-0 w-5 h-5 rounded-full ${tone.ring} border border-[#282D3D] flex items-center justify-center`}
              >
                <Icon className={`w-3 h-3 ${tone.text}`} />
              </span>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-bold text-white leading-tight">
                  {ev.title}
                </span>
                <span className="text-[10px] text-gray-500 shrink-0 tabular-nums">
                  {formatRelative(ev.created_at)}
                </span>
              </div>
              {ev.description && (
                <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">
                  {ev.description}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
